/**
 * Protected Route Component
 *
 * Handles authentication, email verification, and onboarding redirects.
 *
 * @module components/auth/ProtectedRoute
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PageSpinner } from '@/components/ui';

// =============================================================================
// Types
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Whether email verification is required (default: true) */
  requireEmailVerified?: boolean;
  /** Whether onboarding completion is required (default: true) */
  requireOnboarding?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function ProtectedRoute({
  children,
  requireEmailVerified = true,
  requireOnboarding = true,
}: ProtectedRouteProps) {
  const { firebaseUser, user, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while determining auth state
  if (loading) {
    return <PageSpinner />;
  }

  // Redirect to login if not authenticated
  if (!firebaseUser) {
    // Save the intended destination for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ---------------------------------------------------------------------------
  // Email Verification Check
  // ---------------------------------------------------------------------------

  if (requireEmailVerified && !firebaseUser.emailVerified) {
    // User needs to verify email first
    return <Navigate to="/verify-email" replace />;
  }

  // If on verify-email page but already verified, redirect to onboarding
  if (!requireEmailVerified && firebaseUser.emailVerified && location.pathname === '/verify-email') {
    return <Navigate to="/onboarding" replace />;
  }

  // ---------------------------------------------------------------------------
  // Onboarding Check
  // ---------------------------------------------------------------------------

  // Wait for user data to load before checking onboarding status
  if (requireOnboarding && user) {
    const isNewUser = !user.firstName && !user.onboardingCompletedAt;

    if (isNewUser) {
      // Route invited users to simplified onboarding flow
      if (user.signupSource === 'invitation') {
        return <Navigate to="/invited-onboarding" replace />;
      }
      // Regular users go to full onboarding
      return <Navigate to="/onboarding" replace />;
    }
  }

  // If on onboarding page but already completed, redirect to overview
  if (!requireOnboarding && user?.onboardingCompletedAt && location.pathname === '/onboarding') {
    return <Navigate to="/overview" replace />;
  }

  return <>{children}</>;
}
